import { computed } from 'mobx';

import { IUserState, UserMatchResultType, UserStatusKey } from '~api/RampPgApiSdk';
import { UserIdType } from '~domain/entities/Config';
import { ConfigRepository } from '~repositories/ConfigRepository';
import { UserRepository } from '~repositories/UserRepository';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/RampService';

// TODO: APACRIGEL-2052 update UserStateResolverService to only support
// user response from POST user result (not to support POST auth code response)
// Service to reuse the logic which helps resolve account matching status based on "users" returned from POST /user API
export class UserStateResolverService {
    public users: UserMatchResultType[];
    private userRepository: UserRepository;
    private configRepository: ConfigRepository;

    constructor(users: UserMatchResultType[]) {
        this.users = users;
        this.userRepository = diContainer.get(serviceNames.userRepository);
        this.configRepository = diContainer.get(serviceNames.configRepository);
    }

    // TODO: APACRIGEL-2052 rename to existingCompleteUsers
    @computed
    public get completeUsers() {
        return this.users.filter(({ userStatusKey }) =>
            [UserStatusKey.EXISTING_COMPLETE, UserStatusKey.EXISTING_COMPLETE_OFFLINE].includes(
                userStatusKey
            )
        );
    }

    @computed
    public get isCompleteUserFound() {
        return this.completeUsers?.length > 0;
    }

    @computed
    public get completeUserIds() {
        if (!this.isCompleteUserFound) {
            return [];
        }

        return this.completeUsers.reduce<number[]>((userIds, { userId }) => {
            if (userId) {
                return [...userIds, userId];
            }

            return userIds;
        }, []);
    }

    @computed
    public get isMultipleAccountsFound() {
        return (
            new Set(this.users.filter((user) => !!user.userId).map((user) => user.userId)).size > 1
        );
    }

    // Email address or mobile number used to sign-in when complete user is matched. Currently only email address is supported
    @computed
    public get signInAccountIdentifier() {
        const { userFormStore } = this.userRepository;
        const { emailIdentifierFieldName } = this.configRepository;

        if (this.hasCompleteUserByEmail()) {
            return userFormStore.values[emailIdentifierFieldName as string] ?? '';
        }

        return '';
    }

    // Provided all identical identifier values as the DB existing account, so during social consent flow, updating/connect is allowed even not signed in
    @computed
    public get isExactMatch() {
        const {
            iamConfig: { identifiers }
        } = this.configRepository;

        // It's not usable for TW instore who has two identifiers but email is optional
        if (this.users.length !== identifiers.length) {
            return false;
        }

        const userIdSet = new Set(this.users.map((user) => user.userId));

        if (userIdSet.size > 1) {
            return false;
        }

        const userId = [...userIdSet][0];

        return identifiers.every((identifier) =>
            this.users.find((user) => user.userIdType === identifier && user.userId === userId)
        );
    }

    private hasCompleteUserByEmail() {
        // 'completeUsers' comes from response of post session api or post/put user api.
        // post session api returns 'emailAddress' on userIdType, but post/put user api return 'pcEmailAddress' basing on site identifiers
        return this.completeUsers.some((user) => user.userIdType !== UserIdType.MOBILE_NUMBER);
    }

    public hasPartialProtectedUser() {
        return this.users.some(
            (user) => user.userStatusKey === UserStatusKey.EXISTING_PARTIAL_OFFLINE
        );
    }

    public hasNonLoyaltyCompleteUser = () => {
        return (this.completeUsers as IUserState[]).some((user) => !user.loyaltyGlobalIdentifier);
    };

    // This means only one complete user result found, and it is matched by mobile
    public get matchedCompleteUserByMobileOnly() {
        return (
            this.completeUsers.length === 1 &&
            this.completeUsers[0].userIdType === UserIdType.MOBILE_NUMBER
        );
    }
}
